import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ExportState, ExportPreset, Platform, ExportFormat, AssetSize, DeliveryMethod, AdvancedSettings,
} from './types';
import { useAdSession } from '../../hooks/useAdSession';
import ExportPresets from './components/ExportPresets';
import PlatformSelector from './components/PlatformSelector';
import ExportFormatSection from './components/ExportFormat';
import AssetVariations from './components/AssetVariations';
import IncludedContent from './components/IncludedContent';
import DeliveryMethodSection from './components/DeliveryMethod';
import AdvancedSettingsSection from './components/AdvancedSettings';
import ExportPreviewPanel from './components/ExportPreviewPanel';
import ExportSuccessModal from './components/ExportSuccessModal';
import UploadGuideDrawer from './components/UploadGuideDrawer';

const DEFAULT_STATE: ExportState = {
  selectedPlatforms: ['facebook_feed', 'instagram_feed'],
  exportFormat: 'pack',
  selectedSizes: ['square', 'landscape'],
  autoAdapt: true,
  includedContent: ['image', 'headline', 'primary_text', 'cta'],
  deliveryMethod: 'zip',
  advanced: {
    fileType: 'png',
    quality: 'high',
    background: 'original',
    animDuration: 8,
    compression: true,
    safeZone: false,
    cropGuides: false,
  },
};

function buildFileCount(state: ExportState): number {
  let count = 0;
  if (state.includedContent.includes('image')) count += state.selectedSizes.length;
  if (state.includedContent.includes('video') && state.exportFormat !== 'static') count += 1;
  if (state.exportFormat === 'pack') {
    count += 2; // ad-copy.txt + platform-instructions.txt
  }
  if (state.includedContent.includes('brand')) count += 1;
  return Math.max(count, 1);
}

// Empty state when no ad is selected
function NoAdSelectedState() {
  return (
    <div className="flex-1 flex items-center justify-center px-6 py-12">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-stone-900 border border-stone-800 flex items-center justify-center">
          <i className="ri-image-line text-stone-600 text-3xl" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">No Ad Selected</h2>
        <p className="text-stone-400 text-sm mb-6 leading-relaxed">
          Select an ad creative from your recent generations to configure and export platform-ready assets.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/dashboard/generate"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-sm font-semibold transition-all"
          >
            <i className="ri-flashlight-fill" />
            Generate New Ad
          </Link>
          <Link
            to="/dashboard/history"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-300 text-sm font-semibold transition-all"
          >
            <i className="ri-history-line" />
            View History
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ExportPage() {
  const navigate = useNavigate();
  const { result, editedUrls, generationId } = useAdSession();
  const [state, setState] = useState<ExportState>(DEFAULT_STATE);
  const [activePreset, setActivePreset] = useState<string | null>('meta_starter');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [presetSaved, setPresetSaved] = useState(false);
  const [showNewConfirm, setShowNewConfirm] = useState(false);

  // Check if we have a selected ad
  const hasSelectedAd = result !== null && generationId !== null;

  const set = <K extends keyof ExportState>(key: K, val: ExportState[K]) => {
    setState((prev) => ({ ...prev, [key]: val }));
    setActivePreset(null);
  };

  const applyPreset = (preset: ExportPreset) => {
    setState((prev) => ({
      ...prev,
      selectedPlatforms: preset.platforms,
      exportFormat: preset.format,
      selectedSizes: preset.sizes,
      includedContent: preset.content,
    }));
    setActivePreset(preset.id);
  };

  const handleGenerate = () => {
    if (state.selectedPlatforms.length === 0) return;
    setGenerating(true);
    setTimeout(() => {
      setGenerating(false);
      setShowSuccess(true);
    }, 2200);
  };

  const handleSavePreset = () => {
    setPresetSaved(true);
    setTimeout(() => setPresetSaved(false), 2500);
  };

  const fileCount = buildFileCount(state);

  // If no ad selected, show empty state
  if (!hasSelectedAd) {
    return (
      <div className="flex-1 overflow-y-auto bg-stone-950 text-white">
        {/* Top bar */}
        <div className="border-b border-stone-800 bg-stone-950 px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-export-line text-orange-400 text-sm" />
                </div>
                <h1 className="text-lg font-bold text-white">Export Your Ad</h1>
              </div>
              <p className="text-sm text-stone-500">Turn your finished creative into platform-ready assets for every channel.</p>
            </div>
          </div>
        </div>
        <NoAdSelectedState />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-stone-950 text-white">
      {/* Top bar */}
      <div className="border-b border-stone-800 bg-stone-950 px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-export-line text-orange-400 text-sm" />
              </div>
              <h1 className="text-lg font-bold text-white">Export Your Ad</h1>
            </div>
            <p className="text-sm text-stone-500">Turn your finished creative into platform-ready assets for every channel.</p>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setShowGuide(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-300 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-guide-line text-stone-400 text-sm" />
              </div>
              Upload Guide
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        {/* Presets */}
        <ExportPresets activePreset={activePreset} onApply={applyPreset} />

        {/* Main 2-col layout */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
          {/* LEFT COLUMN */}
          <div className="flex flex-col gap-0">
            <PlatformSelector
              selected={state.selectedPlatforms}
              onChange={(v) => set('selectedPlatforms', v as Platform[])}
            />
            <ExportFormatSection
              value={state.exportFormat}
              onChange={(v) => set('exportFormat', v as ExportFormat)}
            />
            <AssetVariations
              selected={state.selectedSizes}
              autoAdapt={state.autoAdapt}
              onSizeChange={(v) => set('selectedSizes', v as AssetSize[])}
              onAutoAdaptChange={(v) => set('autoAdapt', v)}
            />
            <IncludedContent
              selected={state.includedContent}
              onChange={(v) => set('includedContent', v)}
            />
            <DeliveryMethodSection
              value={state.deliveryMethod}
              onChange={(v) => set('deliveryMethod', v as DeliveryMethod)}
            />
            <AdvancedSettingsSection
              settings={state.advanced}
              onChange={(v) => set('advanced', v as AdvancedSettings)}
            />
          </div>

          {/* RIGHT COLUMN */}
          <div className="lg:block">
            <ExportPreviewPanel 
              state={state} 
              adResult={result}
              editedUrls={editedUrls}
            />
          </div>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="sticky bottom-0 left-0 right-0 border-t border-stone-800 bg-stone-950/95 backdrop-blur-sm px-4 lg:px-8 py-4 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/dashboard/editor')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-300 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-stone-400 text-sm" />
              </div>
              Back to Editor
            </button>

            {/* Start New — with inline confirm */}
            <div className="relative">
              <button
                onClick={() => setShowNewConfirm((o) => !o)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-400 hover:text-stone-200 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
                title="Clear this project and start fresh"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-restart-line text-stone-400 text-sm" />
                </div>
                Start New
              </button>
              {showNewConfirm && (
                <div className="absolute bottom-full left-0 mb-2 w-64 bg-stone-800 border border-stone-700 rounded-xl p-4 shadow-2xl z-50">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-500/20 shrink-0">
                      <i className="ri-alert-line text-amber-400 text-sm" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-white leading-snug">Start a new project?</p>
                      <p className="text-xs text-stone-400 mt-1 leading-relaxed">You&apos;ll be taken to the Ad Generator to create a fresh campaign.</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setShowNewConfirm(false); navigate('/dashboard/generate'); }}
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-flashlight-fill" />
                      Go to Generator
                    </button>
                    <button
                      onClick={() => setShowNewConfirm(false)}
                      className="flex-1 py-2 rounded-lg bg-stone-700 hover:bg-stone-600 text-stone-300 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={handleSavePreset}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-300 text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${presetSaved ? 'ri-check-line text-green-400' : 'ri-save-line text-stone-400'} text-sm`} />
              </div>
              {presetSaved ? 'Saved!' : 'Save Export Preset'}
            </button>
          </div>

          <div className="flex items-center gap-3">
            {state.selectedPlatforms.length === 0 && (
              <span className="text-xs text-amber-400 hidden sm:block">Select at least one platform to continue</span>
            )}
            <button
              onClick={handleGenerate}
              disabled={state.selectedPlatforms.length === 0 || generating}
              className={`flex items-center gap-2.5 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                state.selectedPlatforms.length === 0 || generating
                  ? 'bg-stone-700 text-stone-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white'
              }`}
            >
              {generating ? (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line text-sm animate-spin" />
                  </div>
                  Generating...
                </>
              ) : (
                <>
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-export-line text-sm" />
                  </div>
                  Generate Export Package
                  <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{fileCount}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showSuccess && (
        <ExportSuccessModal
          fileCount={fileCount}
          onClose={() => setShowSuccess(false)}
          onOpenUploadGuide={() => setShowGuide(true)}
          adResult={result}
          editedUrls={editedUrls}
          state={state}
        />
      )}
      <UploadGuideDrawer open={showGuide} onClose={() => setShowGuide(false)} />
    </div>
  );
}
